import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { ContextMenuCloseProvider, useContextMenuClose } from "./ContextMenuCloseContext";

import { Button } from "../Button/Button";
import { Icon } from "react-migration/components/Icon";
import { Theme } from "react-migration/lib/theme/Theme";
import { Tooltip } from "react-migration/components/Tooltip";
import clsx from "clsx";
import { useIsMobile } from "react-migration/lib/theme/hooks/useIsMobile";
import { useState } from "react";

export enum MenuItemType {
  ITEM = "item",
  ITEM_WITH_SUBMENU = "item-with-submenu",
  ITEM_WITH_SUBMENU_COMPONENT = "item-with-submenu-component",
  SEPARATOR = "separator",
}

export enum MenuItemVariant {
  REGULAR = "regular",
  DESTRUCTIVE = "destructive",
}

export type MenuItemProps = {
  type: MenuItemType;
  testId: string;
  disabled?: boolean;
  variant?: MenuItemVariant;
  leadElement?: JSX.Element;
  tailElement?: JSX.Element;
  label?: string;
  subLabel?: string;
  onClick?: () => void;
  tooltip?: string;
  children?: JSX.Element | MenuItemProps[];
  externalLink?: boolean;
  customLabel?: JSX.Element;
  isHidden?: boolean;
  details?: string;
};

export const leadElementIconColor = "text-platform-blue-950";

export const SEPARATOR = {
  type: MenuItemType.SEPARATOR,
  testId: "separator",
} as const;

export const MenuItem = (props: MenuItemProps) => {
  const { testId, disabled, variant, onClick } = props;
  return (
    <DropdownMenu.Item
      data-testid={testId}
      disabled={disabled}
      className={clsx(
        "relative flex items-start gap-2 px-1.5 py-1 my-0.5 mx-1.5 text-sm select-none outline-hidden rounded-md",
        disabled
          ? "cursor-not-allowed text-text-base-primary-disabled bg-neutral-100"
          : !onClick
          ? "cursor-default hover:bg-transparent"
          : "cursor-pointer",
        variant === MenuItemVariant.DESTRUCTIVE
          ? "data-highlighted:bg-negative-50 text-text-status-destructive"
          : "data-highlighted:bg-informative-50 text-neutral-800"
      )}
      onClick={(e) => {
        e.stopPropagation();
        if (disabled || !onClick) return;
        onClick();
      }}
    >
      <MenuItemContents {...props} />
    </DropdownMenu.Item>
  );
};

export const MenuItemMobile = (
  props: MenuItemProps & {
    isTop?: boolean;
    isBottom?: boolean;
    noBorder?: boolean;
  }
) => {
  const { testId, type, disabled, variant, isTop, isBottom, onClick, noBorder } = props;
  const closeContextMenu = useContextMenuClose();
  return (
    <div
      data-testid={testId}
      aria-disabled={disabled}
      className={clsx(
        "flex p-2 select-none outline-hidden",
        disabled && "text-text-base-primary-disabled bg-background-button-secondary-disabled",
        variant === MenuItemVariant.DESTRUCTIVE
          ? "data-highlighted:bg-negative-50 text-text-status-destructive"
          : "data-highlighted:bg-informative-50 text-neutral-800",
        isTop && "rounded-t-lg",
        isBottom ? "rounded-b-lg" : noBorder ? "" : "border-b border-border-base-divider"
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled || !onClick) return;
        onClick();
        if (type === MenuItemType.ITEM) {
          closeContextMenu();
        }
      }}
    >
      <MenuItemContents {...props} />
    </div>
  );
};

export const MenuItemContents = (props: MenuItemProps) => {
  const { customLabel, details, externalLink, label, subLabel, leadElement, tailElement, tooltip, variant } = props;
  return (
    <>
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          {leadElement ? leadElement : null}
          <div
            className={clsx("flex flex-col", variant === MenuItemVariant.DESTRUCTIVE && "text-text-status-destructive")}
          >
            <span className="text-sm">{label}</span>
            {subLabel && subLabel}
          </div>
        </div>
        {details && <span className="block mt-1 text-xs text-text-base-secondary/50">{details}</span>}
      </div>

      {tooltip && (
        <div className="relative flex justify-between">
          <Tooltip.Wrapper>
            <Tooltip.Trigger>
              <i className="text-xl leading-none cursor-pointer icon-lt-information-line text-text-base-active" />
            </Tooltip.Trigger>
            <Tooltip.Content theme={Theme.Dark} size="sm">
              {tooltip}
            </Tooltip.Content>
          </Tooltip.Wrapper>
        </div>
      )}
      {customLabel}
      {externalLink && <Icon icon="icon-lt-external-link-line" size="sm" extraClasses="ml-auto" />}
      {tailElement}
    </>
  );
};

export const MenuItemWithSubmenu = (props: MenuItemProps) => {
  const { children, disabled, testId, type, variant } = props;
  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger
        data-testid={testId}
        className={clsx(
          "relative flex items-start gap-2 px-1.5 py-1 my-0.5 mx-1.5 text-sm select-none outline-hidden",
          disabled ? "cursor-not-allowed text-text-base-primary-disabled" : "cursor-pointer",
          variant === MenuItemVariant.DESTRUCTIVE
            ? "data-highlighted:bg-negative-50 text-text-status-destructive"
            : "data-highlighted:bg-informative-50 text-neutral-800"
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
        }}
      >
        <MenuItemContents tailElement={<Icon icon="icon-lt-arrow-right-s-line" extraClasses="ml-auto" />} {...props} />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          className={clsx(
            "relative z-50 flex flex-col overflow-hidden bg-white border rounded-lg shadow-md border-blue-grey-200",
            type === MenuItemType.ITEM_WITH_SUBMENU_COMPONENT ? "w-auto p-0" : "w-60 py-1"
          )}
          sideOffset={2}
          alignOffset={-5}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
          }}
        >
          {type === MenuItemType.ITEM_WITH_SUBMENU_COMPONENT ? (
            <>{children}</>
          ) : (
            <MenuItemsList items={children as MenuItemProps[]} />
          )}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
};

const MenuWithSubMenuMobile = (
  props: MenuItemProps & {
    isTop?: boolean;
    isBottom?: boolean;
    noBorder?: boolean;
  }
) => {
  const { children, type, noBorder } = props;
  const [submenuOpen, setSubmenuOpen] = useState(false);
  if (submenuOpen) {
    return (
      <div className="w-full h-full flex flex-col absolute top-0 left-0 bg-white rounded-t-2xl px-4">
        <div className="p-4 pl-2 font-bold">
          <Button onClick={() => setSubmenuOpen(false)} variant="ghost" leadIcon="lt-arrow-left-s-line">
            Back
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {type === MenuItemType.ITEM_WITH_SUBMENU_COMPONENT ? (
            <>{children}</>
          ) : (
            <MenuItemsList items={children as MenuItemProps[]} noBorder={noBorder} />
          )}
        </div>
      </div>
    );
  }
  return (
    <MenuItemMobile
      tailElement={<Icon icon="icon-lt-arrow-right-s-line" extraClasses="ml-auto" />}
      onClick={() => setSubmenuOpen(true)}
      {...props}
    />
  );
};

export function MenuItems({
  items,
  onClose,
  noBorder,
}: {
  items: MenuItemProps[];
  onClose: () => void;
  noBorder?: boolean;
}) {
  return (
    <ContextMenuCloseProvider closeMenu={onClose}>
      <MenuItemsList items={items} noBorder={noBorder} />
    </ContextMenuCloseProvider>
  );
}

function MenuItemsList({ items, noBorder }: { items: MenuItemProps[]; noBorder?: boolean }) {
  const isMobile = useIsMobile();
  // Group items between separators
  const groups: MenuItemProps[][] = [];
  let currentGroup: MenuItemProps[] = [];
  items
    .filter((item) => !item.isHidden)
    .forEach((item) => {
      if (item.type === MenuItemType.SEPARATOR) {
        if (currentGroup.length) groups.push(currentGroup);
        currentGroup = [];
        groups.push([item]);
      } else {
        currentGroup.push(item);
      }
    });
  if (currentGroup.length) groups.push(currentGroup);
  return groups.flatMap((group, groupIdx) => {
    if (group.length === 1 && group[0].type === MenuItemType.SEPARATOR) {
      return isMobile ? (
        <div className="py-2" key={`separator-${groupIdx}`}>
          <div className="w-full h-px bg-border-action-disabled" />
        </div>
      ) : (
        <DropdownMenu.Separator key={`separator-${groupIdx}`} className="w-auto h-px my-1 bg-border-base-divider" />
      );
    } else {
      return group.map((item, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === group.length - 1;
        if (item.type === MenuItemType.ITEM_WITH_SUBMENU || item.type === MenuItemType.ITEM_WITH_SUBMENU_COMPONENT) {
          return isMobile ? (
            <MenuWithSubMenuMobile key={item.label} {...item} isTop={isFirst} isBottom={isLast} noBorder={noBorder} />
          ) : (
            <MenuItemWithSubmenu key={item.label} {...item} />
          );
        } else if (item.type === MenuItemType.ITEM) {
          return isMobile ? (
            <MenuItemMobile key={item.label} {...item} isTop={isFirst} isBottom={isLast} noBorder={noBorder} />
          ) : (
            <MenuItem key={item.label} {...item} />
          );
        }
        return null;
      });
    }
  });
}
